// Delete/Redo confirmation modals + attachment gallery (extracted from ui.js, B4).
//
import { applyRotation, rotateButtons, turnThumbnail } from './image-rotate.js';
import { mediaUrl } from './api.js';
// Self-contained: owns deleteModalState/redoModalState and only depends on the shared
// app state and getHeaders. ui.js imports the entry points it uses and re-exports the
// public ones (facade) so external importers are unchanged.
import { state } from './state.js';
import { getHeaders } from './api.js';
// These ui.js render helpers are invoked at call-time inside modal handlers; the
// resulting ui.js <-> ui-modals.js cycle is safe (bindings resolved before use).
import {
    updateRedoButtonVisibility,
    updateNarratorDeleteButtons,
    updateModeIndicators,
} from './ui.js';

let deleteModalState = {
    messageId: null,
    messageEl: null
};

/**
 * Initialize delete confirmation modal handlers
 */
export function initDeleteModal() {
    const modal = document.getElementById('delete-modal');
    const closeBtn = document.getElementById('delete-modal-close');
    const cancelBtn = document.getElementById('delete-modal-cancel');
    const confirmBtn = document.getElementById('delete-modal-confirm');

    if (!modal) return;

    // Close handlers
    closeBtn?.addEventListener('click', hideDeleteModal);
    cancelBtn?.addEventListener('click', hideDeleteModal);

    // Click outside to close
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            hideDeleteModal();
        }
    });

    // Confirm handler
    confirmBtn?.addEventListener('click', handleDeleteConfirm);

    // ESC key to close
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            hideDeleteModal();
        }
    });

    // Expose to window for button click handlers
    window.showDeleteConfirmModal = showDeleteConfirmModal;
}

/**
 * Show the delete confirmation modal
 */
export function showDeleteConfirmModal(messageId, messageEl) {
    // Check if delete is disabled (e.g., narrator mode restriction)
    const deleteBtn = messageEl?.querySelector('.delete-btn');
    if (deleteBtn?.classList.contains('narrator-disabled')) {
        return; // Don't show modal if delete is restricted
    }

    deleteModalState.messageId = messageId;
    deleteModalState.messageEl = messageEl;

    const modal = document.getElementById('delete-modal');
    const narratorSection = document.getElementById('delete-narrator-section');
    const chunkInfoEl = document.getElementById('delete-chunk-info');

    // Check if this message has narrator chunk info
    const narratorChunkId = messageEl?.dataset?.narratorChunkId;
    const narratorTotalChunks = messageEl?.dataset?.narratorTotalChunks;
    const narratorChunkCount = parseInt(messageEl?.dataset?.narratorChunkCount || '1');
    const narratorSection_name = messageEl?.dataset?.narratorSection;

    if (narratorSection) {
        if (narratorChunkId && narratorTotalChunks) {
            // Show narrator section with chunk range info
            narratorSection.classList.remove('hidden');
            if (chunkInfoEl) {
                const sectionName = narratorSection_name ? `${narratorSection_name} - ` : '';
                const startChunk = parseInt(narratorChunkId);
                const endChunk = startChunk + narratorChunkCount - 1;
                const chunkRange = narratorChunkCount > 1
                    ? `Chunks ${startChunk}-${endChunk}`
                    : `Chunk ${startChunk}`;
                chunkInfoEl.textContent = `${sectionName}${chunkRange} of ${narratorTotalChunks}`;
            }
        } else {
            // Hide narrator section
            narratorSection.classList.add('hidden');
        }
    }

    // Offer to take the download links with it, when there are any.
    const permalinkSection = document.getElementById('delete-permalink-section');
    const permalinkCount = document.getElementById('delete-permalink-count');
    const permalinkBox = document.getElementById('delete-permalink-checkbox');
    let links = [];
    try {
        links = JSON.parse(messageEl?.querySelector('.message-actions')?.dataset.permalinks || '[]');
    } catch {
        links = [];
    }
    if (permalinkSection) {
        permalinkSection.classList.toggle('hidden', links.length === 0);
        if (permalinkBox) permalinkBox.checked = true;
        if (permalinkCount && links.length) {
            permalinkCount.textContent =
                links.length === 1 ? 'its download link' : `its ${links.length} download links`;
        }
    }

    if (modal) {
        modal.classList.remove('hidden');
        modal.classList.add('active');
    }
}

/**
 * Hide the delete confirmation modal
 */
function hideDeleteModal() {
    const modal = document.getElementById('delete-modal');
    if (modal) {
        modal.classList.remove('active');
        modal.classList.add('hidden');
    }

    // Reset narrator section
    const narratorSection = document.getElementById('delete-narrator-section');
    if (narratorSection) narratorSection.classList.add('hidden');

    deleteModalState.messageId = null;
    deleteModalState.messageEl = null;
}

/**
 * Handle delete confirmation
 */
async function handleDeleteConfirm() {
    const { messageId, messageEl } = deleteModalState;
    if (!messageId || !messageEl) {
        hideDeleteModal();
        return;
    }

    const confirmBtn = document.getElementById('delete-modal-confirm');
    if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Deleting...';
    }

    try {
        // Import state and auth dynamically to avoid circular dependencies
        const { state, saveNarratorProgress } = await import('./state.js');
        const { getHeaders } = await import('./api.js');

        const sessionId = state.sessionId;
        if (!sessionId) {
            throw new Error('No active session');
        }

        // Call delete API
        // Default true; false only when the user deliberately unticked it.
        const keepLinks =
            document.getElementById('delete-permalink-checkbox')?.checked === false;
        const response = await fetch(
            `/sessions/${sessionId}/messages/${messageId}?delete_permalinks=${!keepLinks}`,
            { method: 'DELETE', headers: getHeaders(false) }
        );

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.detail || `Delete failed: ${response.status}`);
        }

        const result = await response.json();

        // Handle narrator mode revert — always step back on delete
        const narratorChunkId = messageEl?.dataset?.narratorChunkId;
        const narratorBook = messageEl?.dataset?.narratorBook;
        const narratorKbPath = messageEl?.dataset?.narratorKbPath;

        if (narratorChunkId && state.narratorMode) {
            const chunkId = parseInt(narratorChunkId, 10);

            // Update local state
            state.narratorState.chunk_id = chunkId;
            if (narratorBook) state.narratorState.book = narratorBook;
            if (narratorKbPath) state.narratorState.kb_path = narratorKbPath;

            // Update server-side narrator position
            if (state.loadedAgent && narratorBook && narratorKbPath) {
                try {
                    const { setNarratorPosition } = await import('./api.js');
                    await setNarratorPosition(state.loadedAgent, narratorBook, narratorKbPath, chunkId);
                    // Position reverted successfully
                } catch (e) {
                    console.error('[Delete] Failed to revert server narrator position:', e);
                }
            }

            // Save reverted progress to localStorage
            saveNarratorProgress(sessionId, state.narratorState);
        }

        // Remove the message element BEFORE updating badge
        if (messageEl && messageEl.parentNode) {
            // Pair semantics apply ONLY to real assistant responses: deleting
            // one also removes the user prompt that produced it (mirrors the
            // backend's pair-delete branch). Dictations are user-role rows
            // STYLED with the assistant+dictation-message classes, and the
            // backend deletes them standalone — so they must delete ALONE
            // here too. Blindly removing the preceding .user sibling visually
            // nuked the previous dictation as well (recovered on reload,
            // since the DB never deleted it). The sibling check also refuses
            // to treat a dictation as a "pair prompt" for the same reason.
            const isRealAssistant =
                messageEl.classList.contains('assistant') &&
                !messageEl.classList.contains('dictation-message');
            if (isRealAssistant) {
                const prevSibling = messageEl.previousElementSibling;
                if (
                    prevSibling &&
                    prevSibling.classList.contains('user') &&
                    !prevSibling.classList.contains('dictation-message')
                ) {
                    prevSibling.remove();
                }
            }
            messageEl.remove();

            // Update redo button visibility after deletion
            updateRedoButtonVisibility();

            // Update narrator mode delete buttons (next message becomes deletable)
            updateNarratorDeleteButtons();

            // Update badge AFTER element is removed so count is correct
            if (state.narratorMode) {
                updateModeIndicators(
                    state.loadedAgent,
                    state.loadedCollection,
                    state.narratorMode,
                    state.narratorState.chunk_id
                );
            }
        }

    } catch (err) {
        console.error('Delete failed:', err);
        alert(`Failed to delete message: ${err.message}`);
    } finally {
        if (confirmBtn) {
            confirmBtn.disabled = false;
            confirmBtn.textContent = 'Delete';
        }
        hideDeleteModal();
    }
}

// ============================================================================
// Redo/Edit Prompt Modal
// ============================================================================

let redoModalState = {
    messageId: null,
    messageEl: null,
    originalPrompt: '',
    attachments: []  // Array of {id, type, name, content, size}
};

/**
 * Initialize redo modal handlers
 */
export function initRedoModal() {
    const modal = document.getElementById('redo-modal');
    const closeBtn = document.getElementById('redo-modal-close');
    const cancelBtn = document.getElementById('redo-modal-cancel');
    const confirmBtn = document.getElementById('redo-modal-confirm');
    const promptInput = document.getElementById('redo-prompt-input');

    if (!modal) return;

    // Close handlers
    closeBtn?.addEventListener('click', hideRedoModal);
    cancelBtn?.addEventListener('click', hideRedoModal);

    // Click outside to close
    modal.addEventListener('click', (e) => {
        if (e.target === modal) {
            hideRedoModal();
        }
    });

    // Confirm handler
    confirmBtn?.addEventListener('click', handleRedoConfirm);

    // ESC key to close
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('active')) {
            hideRedoModal();
        }
    });

    // Handle paste events for attachments
    promptInput?.addEventListener('paste', handleRedoPaste);

    // File-picker: choose/swap an image without pasting (reuses the paste image path)
    const chooseImageBtn = document.getElementById('redo-choose-image');
    const imageInput = document.getElementById('redo-image-input');
    chooseImageBtn?.addEventListener('click', () => imageInput?.click());
    imageInput?.addEventListener('change', (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.type.startsWith('video/')) processRedoVideoFile(file);
            else processRedoImageFile(file);
        }
        e.target.value = '';  // allow re-selecting the same file
    });

    // Expose to window for button click handlers
    window.showRedoModal = showRedoModal;
}

/**
 * Show the redo modal with the original prompt
 */
export function showRedoModal(messageId, messageEl) {
    redoModalState.messageId = messageId;
    redoModalState.messageEl = messageEl;
    redoModalState.attachments = [];

    // Find the user message before this assistant message to get the original prompt
    const prevSibling = messageEl?.previousElementSibling;
    let originalPrompt = '';
    let originalAttachments = [];

    if (prevSibling && prevSibling.classList.contains('user')) {
        // Get the text content (excluding attachment chips)
        const contentDiv = prevSibling.querySelector('.message-content');
        if (contentDiv) {
            // Clone and remove attachment display elements to get just the text
            const clone = contentDiv.cloneNode(true);
            clone.querySelectorAll('.attachment-chips').forEach(el => el.remove());
            originalPrompt = clone.textContent.trim();
        }

        // Attachments ride on the element as a JS property (set by
        // ui.js addMessage for live sends AND history re-renders).
        if (Array.isArray(prevSibling._attachments)) {
            originalAttachments = prevSibling._attachments;
        } else if (prevSibling.dataset.attachments) {
            try {
                originalAttachments = JSON.parse(prevSibling.dataset.attachments);
            } catch {
            }
        }
    }

    redoModalState.originalPrompt = originalPrompt;
    redoModalState.attachments = [...originalAttachments];

    // Populate the modal
    const promptInput = document.getElementById('redo-prompt-input');
    if (promptInput) {
        promptInput.value = originalPrompt;
    }

    // Render attachments
    renderRedoAttachments();

    // Promptless image response variant: the original turn was image-only. Make it clear
    // you can add a prompt (-> normal text response) OR swap the image (-> re-describe).
    const isPromptlessImage =
        !originalPrompt &&
        originalAttachments.some(a => a.type === 'image' || a.type === 'video');
    const hint = document.getElementById('redo-modal-hint');
    if (hint) {
        hint.textContent = isPromptlessImage
            ? 'Add a prompt for a normal response, or swap the image to re-describe it. Leave the prompt empty to just re-describe.'
            : 'Edit your prompt and resubmit. This will delete the current response and generate a new one.';
    }
    if (promptInput) {
        promptInput.placeholder = isPromptlessImage
            ? 'Optional: add a prompt, or leave empty to re-describe the image…'
            : 'Enter your prompt... (Paste code or text to add attachments)';
    }

    const modal = document.getElementById('redo-modal');
    if (modal) {
        modal.classList.remove('hidden');
        modal.classList.add('active');
        // Focus the input
        promptInput?.focus();
    }
}

/**
 * Hide the redo modal
 */
function hideRedoModal() {
    const modal = document.getElementById('redo-modal');
    if (modal) {
        modal.classList.remove('active');
        modal.classList.add('hidden');
    }
    redoModalState.messageId = null;
    redoModalState.messageEl = null;
    redoModalState.originalPrompt = '';
    redoModalState.attachments = [];

    // Clear the attachments container
    const container = document.getElementById('redo-attachments-container');
    if (container) container.innerHTML = '';
}

/**
 * Render attachments in the redo modal
 */
/**
 * Upload a chosen video and swap it into the redo attachments — same
 * server path as the composer, minus the progress bar (modal videos are
 * usually short retakes; a spinner label on the chip is enough).
 */
async function processRedoVideoFile(file) {
    const { getHeaders } = await import('./api.js');
    redoModalState.attachments = redoModalState.attachments.filter(
        a => a.type !== 'video' && a.type !== 'image'
    );
    const placeholder = { type: 'video', name: `${file.name} (uploading\u2026)`, content: '', size: file.size };
    redoModalState.attachments.push(placeholder);
    renderRedoAttachments();
    try {
        const form = new FormData();
        form.append('file', file, file.name || 'video.mp4');
        const headers = { ...(getHeaders(false) || {}) };
        delete headers['Content-Type'];
        const res = await fetch('/chat/media/upload', { method: 'POST', headers, body: form });
        const body = await res.json();
        if (!res.ok) throw new Error(body.detail || `Upload failed (${res.status})`);
        Object.assign(placeholder, {
            name: file.name,
            content: body.media_id,
            media_type: body.media_type,
            size: body.size ?? file.size,
            duration: body.duration,
        });
    } catch (err) {
        redoModalState.attachments = redoModalState.attachments.filter(a => a !== placeholder);
        alert(err.message || 'Video upload failed.');
    }
    renderRedoAttachments();
}

function renderRedoAttachments() {
    const container = document.getElementById('redo-attachments-container');
    if (!container) return;

    container.innerHTML = '';

    if (redoModalState.attachments.length === 0) {
        container.style.display = 'none';
        return;
    }

    container.style.display = 'flex';

    redoModalState.attachments.forEach((att, index) => {
        const chip = document.createElement('div');
        if (att.type === 'video' && att.content) {
            chip.className = 'attachment-chip video-chip';
            chip.innerHTML = `
                <img class="attachment-thumbnail" src="${mediaUrl(att.content, 'poster')}" alt="" />
                <span class="attachment-name">${att.name}</span>
                <button class="attachment-remove" data-index="${index}" title="Remove">&times;</button>
            `;
        } else if (att.type === 'image') {
            chip.className = 'attachment-chip image-chip';
            chip.innerHTML = `
                <img class="attachment-thumbnail" src="data:${att.media_type};base64,${att.content}" alt="${att.name}" />
                <span class="attachment-name">${att.name}</span>
                <button class="attachment-remove" data-index="${index}" title="Remove">&times;</button>
            `;
        } else {
            chip.className = 'attachment-chip';
            chip.innerHTML = `
                <span class="attachment-icon">${att.type === 'code' ? '📄' : '📝'}</span>
                <span class="attachment-name">${att.name}</span>
                <span class="attachment-size">(${formatSize(att.size)})</span>
                <button class="attachment-remove" data-index="${index}" title="Remove">&times;</button>
            `;
        }

        // Remove button handler
        chip.querySelector('.attachment-remove').addEventListener('click', (e) => {
            e.stopPropagation();
            const idx = parseInt(e.target.dataset.index, 10);
            redoModalState.attachments.splice(idx, 1);
            renderRedoAttachments();
        });

        container.appendChild(chip);
    });
}

/**
 * Format file size for display
 */
function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Handle paste events in redo modal textarea
 */
function handleRedoPaste(e) {
    // Strategy 1: Check clipboardData.items for image blobs (desktop browsers)
    const items = e.clipboardData?.items;
    if (items) {
        for (const item of items) {
            if (item.type.startsWith('image/')) {
                e.preventDefault();
                const file = item.getAsFile();
                if (file) {
                    processRedoImageFile(file);
                }
                return;
            }
        }
    }

    // Strategy 2: Check clipboardData.files (some mobile browsers)
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
        for (const file of files) {
            if (file.type.startsWith('image/')) {
                e.preventDefault();
                processRedoImageFile(file);
                return;
            }
        }
    }

    // Strategy 3: Check text/html for embedded images (mobile "Copy Image")
    const html = e.clipboardData?.getData('text/html');
    if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const img = doc.querySelector('img[src]');
        if (img) {
            const src = img.getAttribute('src');
            const match = src?.match(/^data:(image\/[^;]+);base64,(.+)$/);
            if (match) {
                e.preventDefault();
                // Create a blob from base64 and process as file
                const byteString = atob(match[2]);
                const bytes = new Uint8Array(byteString.length);
                for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
                const blob = new Blob([bytes], { type: match[1] });
                processRedoImageFile(new File([blob], 'pasted-image', { type: match[1] }));
                return;
            } else if (src?.startsWith('http')) {
                e.preventDefault();
                fetch(src)
                    .then(r => r.blob())
                    .then(blob => {
                        if (blob.type.startsWith('image/')) {
                            processRedoImageFile(new File([blob], 'pasted-image', { type: blob.type }));
                        }
                    })
                    .catch(() => {});
                return;
            }
        }
    }

    const text = e.clipboardData?.getData('text');
    if (!text || text.length < 100) return;  // Only capture large pastes

    // Check if it looks like code or structured text
    const looksLikeCode = /^(import |from |const |let |var |function |class |def |public |private |<\?|<!DOCTYPE|<html|{|}|\[|\])/m.test(text) ||
        text.includes('\n\t') ||
        text.includes('\n    ') ||
        (text.match(/\n/g) || []).length > 5;

    if (looksLikeCode || text.length > 500) {
        e.preventDefault();

        // Generate a unique ID
        const id = `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Determine type and name
        const isCode = looksLikeCode;
        const lines = text.split('\n').length;
        const name = isCode
            ? `Pasted code (${lines} lines)`
            : `Pasted text (${lines} lines)`;

        const attachment = {
            id,
            type: isCode ? 'code' : 'text',
            name,
            content: text,
            size: text.length
        };

        redoModalState.attachments.push(attachment);
        renderRedoAttachments();
    }
}

/**
 * Process an image file pasted into the redo modal
 */
function processRedoImageFile(file) {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) return;

    const reader = new FileReader();
    reader.onload = () => {
        const dataUrl = reader.result;
        const base64 = dataUrl.split(',')[1];
        const mediaType = file.type;
        const name = file.name || `Image ${new Date().toLocaleTimeString()}`;
        const id = `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        redoModalState.attachments.push({
            id,
            type: 'image',
            name,
            content: base64,
            size: file.size,
            media_type: mediaType
        });
        renderRedoAttachments();
    };
    reader.readAsDataURL(file);
}

/**
 * Handle redo confirmation - delete and resubmit
 */
async function handleRedoConfirm() {
    const { messageId, messageEl } = redoModalState;
    const promptInput = document.getElementById('redo-prompt-input');
    const newPrompt = promptInput?.value?.trim();

    // Allow an empty prompt when there's media to re-describe (promptless variant).
    const hasImage = redoModalState.attachments.some(
        a => a.type === 'image' || a.type === 'video'
    );
    if (!messageEl || (!newPrompt && !hasImage)) {
        if (!newPrompt && !hasImage) {
            alert('Enter a prompt, or add an image or video to re-describe.');
        }
        return;
    }

    const confirmBtn = document.getElementById('redo-modal-confirm');
    if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.textContent = 'Processing...';
    }

    try {
        // Import state and auth dynamically to avoid circular dependencies
        const { state } = await import('./state.js');
        const { getHeaders } = await import('./api.js');

        // Promptless IMAGE re-describe needs vision available — check BEFORE
        // the destructive delete so we never delete the pair without a
        // replacement. Videos are exempt twice over: a promptless video
        // always has something to say (its transcript) with no vision at
        // all, and this client-side check cannot see the whole picture
        // anyway — a multimodal worker or the cloud vision lane count, and
        // only the server knows about those. Blocking a video redo here
        // stopped a redo that the server would have handled fine.
        const hasVideo = redoModalState.attachments.some(a => a.type === 'video');
        if (!newPrompt && hasImage && !hasVideo) {
            const { loadedModelSupportsVision } = await import('./ui.js');
            const visionOk =
                !!(state.agentSettings && state.agentSettings.tool_vision) ||
                loadedModelSupportsVision();
            if (!visionOk) {
                alert('Load a vision model or enable the agent vision tool to re-describe an image without a prompt.');
                if (confirmBtn) {
                    confirmBtn.disabled = false;
                    confirmBtn.textContent = 'Resubmit';
                }
                return;
            }
        }

        const sessionId = state.sessionId;

        // Step 1: Delete the current message pair
        if (messageId && sessionId) {
            // Persisted message — delete from DB
            // The resubmission reuses these pictures by id, so the delete must
            // leave their files alone: purging them here handed the describer
            // an empty image and the redo failed with "unknown format".
            const keep = redoModalState.attachments
                .map(a => (a.type === 'video' ? a.content : a.media_id))
                .filter(id => /^[0-9a-f]{32}$/.test(id || ''));
            const keepQs = keep.length ? `?keep_media=${keep.join(',')}` : '';
            const response = await fetch(`/sessions/${sessionId}/messages/${messageId}${keepQs}`, {
                method: 'DELETE',
                headers: getHeaders(false)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Delete failed: ${response.status}`);
            }
        }

        // Remove the message elements from DOM
        if (messageEl && messageEl.parentNode) {
            const prevSibling = messageEl.previousElementSibling;
            if (prevSibling && prevSibling.classList.contains('user')) {
                prevSibling.remove();
            }
            messageEl.remove();
        }

        // Step 2: Reset button and close modal
        if (confirmBtn) {
            confirmBtn.disabled = false;
            confirmBtn.textContent = 'Resubmit';
        }

        // Save attachments before hiding modal clears them
        const attachmentsToSend = [...redoModalState.attachments];

        hideRedoModal();

        // Import sendMessage dynamically
        const { sendMessageWithAttachments } = await import('./chat.js');

        // Send the new message with attachments (no web search for redo)
        await sendMessageWithAttachments(newPrompt, attachmentsToSend);

    } catch (err) {
        console.error('Redo failed:', err);
        alert(`Failed to redo: ${err.message}`);
        if (confirmBtn) {
            confirmBtn.disabled = false;
            confirmBtn.textContent = 'Resubmit';
        }
    }
}

/**
 * Open a lightbox gallery for image attachments with navigation across all conversation images.
 */
export function openAttachmentGallery(clickedThumbEl) {
    // Collect all image thumbnails from the conversation
    const allThumbs = [...document.querySelectorAll('#chat-container .msg-attachment-thumbnail')];
    const images = [];
    let startIndex = 0;

    allThumbs.forEach((thumb, i) => {
        images.push({ thumb, alt: thumb.alt || 'Image' });
        if (thumb === clickedThumbEl) {
            startIndex = i;
        }
    });

    // Fallback: if no thumbnails found in DOM
    if (images.length === 0) {
        images.push({ thumb: clickedThumbEl, alt: clickedThumbEl.alt || 'Image' });
    }

    const existing = document.querySelector('.image-viewer-overlay');
    if (existing) existing.remove();

    let currentIndex = startIndex;

    const overlay = document.createElement('div');
    overlay.className = 'image-viewer-overlay';
    overlay.innerHTML = `
        <button class="image-viewer-nav prev">&#8249;</button>
        <div class="image-viewer-panel">
            <img src="" alt="" />
            <div class="image-viewer-rotate-slot"></div>
            <div class="image-viewer-caption"></div>
            <div class="image-viewer-counter"></div>
            <button class="image-viewer-close">&times;</button>
        </div>
        <button class="image-viewer-nav next">&#8250;</button>
    `;

    const mainImg = overlay.querySelector('.image-viewer-panel img');
    const caption = overlay.querySelector('.image-viewer-caption');
    const counter = overlay.querySelector('.image-viewer-counter');
    const prevBtn = overlay.querySelector('.image-viewer-nav.prev');
    const nextBtn = overlay.querySelector('.image-viewer-nav.next');

    // The full view draws from the thumbnail's ORIGINAL bytes at the
    // thumbnail's current angle, so it is full resolution and always agrees
    // with the small picture it was opened from.
    function render() {
        const img = images[currentIndex];
        const t = img.thumb;
        delete mainImg.dataset.origSrc;
        mainImg.src = t.dataset.origSrc || t.src;
        mainImg.alt = img.alt;
        const deg = Number(t.dataset.rotation) || 0;
        if (deg) applyRotation(mainImg, deg);
        caption.textContent = img.alt;
        counter.textContent = images.length > 1 ? `${currentIndex + 1} / ${images.length}` : '';
        prevBtn.style.visibility = currentIndex > 0 ? 'visible' : 'hidden';
        nextBtn.style.visibility = currentIndex < images.length - 1 ? 'visible' : 'hidden';
    }

    if (!state.capsuleMode) {
        overlay.querySelector('.image-viewer-rotate-slot').appendChild(
            rotateButtons(async (dir) => {
                await turnThumbnail(images[currentIndex].thumb, dir);
                render();
            }, 'image-viewer-rotate')
        );
    }
    // A turn made on the thumbnail while this is open (another tab of the
    // same page, say) is mirrored here too.
    const onRotated = (e) => {
        if (e.detail?.thumb === images[currentIndex].thumb) render();
    };
    document.addEventListener('attachment-rotated', onRotated);

    prevBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (currentIndex > 0) { currentIndex--; render(); }
    });

    nextBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (currentIndex < images.length - 1) { currentIndex++; render(); }
    });

    overlay.addEventListener('click', (e) => {
        if (e.target === overlay || e.target.classList.contains('image-viewer-close')) {
            overlay.remove();
            document.removeEventListener('keydown', handleKeyboard);
            document.removeEventListener('attachment-rotated', onRotated);
        }
    });

    const handleKeyboard = (e) => {
        if (e.key === 'Escape') {
            overlay.remove();
            document.removeEventListener('keydown', handleKeyboard);
        } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
            currentIndex--; render();
        } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
            currentIndex++; render();
        }
    };
    document.addEventListener('keydown', handleKeyboard);

    render();
    document.body.appendChild(overlay);
}
